import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button as MuiButton,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import Modal from '../atoms/modal/modal';
import Button from '../atoms/button';
import {
  describeVinLookupError,
  lookupVehicleByVin,
  VinLookupMatch,
} from '../../api/vehicles';
import { FetchImageFromBackoffice } from '../../utils/requests';
import theme from '../../theme';
import type { EditedVehicleInformation } from './editVehicleInformationModal';
import { SubTitle } from '../atoms/text';

type VinVerificationModalProps = {
  open: boolean;
  onClose: () => void;
  onSaved?: (saved: EditedVehicleInformation) => void;
  vin?: string | null;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  imageId?: string | null;
  vehicleVin?: string | null;
};

const readOnlySlotProps = { htmlInput: { readOnly: true } };

const yearsInRange = (fromYear: number, toYear: number | null) => {
  const start = fromYear;
  const end = toYear ?? new Date().getFullYear();
  const years: number[] = [];
  for (let year = start; year <= end; year += 1) {
    years.push(year);
  }
  return years;
};

const uniqueModels = (matches: VinLookupMatch[]) => {
  const map = new Map<string, VinLookupMatch>();
  for (const match of matches) {
    const modelId = String(match.modelId);
    if (!map.has(modelId)) {
      map.set(modelId, match);
    }
  }
  return Array.from(map.values());
};

const variantName = (match: VinLookupMatch) => String(match.variant ?? '').trim();

const uniqueVariantsForModel = (matches: VinLookupMatch[], modelId: string) => {
  const map = new Map<string, VinLookupMatch>();
  for (const match of matches) {
    if (String(match.modelId) !== modelId) {
      continue;
    }
    const name = variantName(match);
    if (!map.has(name)) {
      map.set(name, match);
    }
  }
  return Array.from(map.values());
};

const VinVerificationModal = ({
  open,
  onClose,
  onSaved,
  vin,
  make,
  model,
  year,
  imageId,
  vehicleVin,
}: VinVerificationModalProps) => {
  const [vinValue, setVinValue] = useState('');
  const [makeValue, setMakeValue] = useState('');
  const [modelValue, setModelValue] = useState('');
  const [yearValue, setYearValue] = useState<number | ''>('');
  const [lookupMatches, setLookupMatches] = useState<VinLookupMatch[]>([]);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [selectedVariantName, setSelectedVariantName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>();
  const [imageLoading, setImageLoading] = useState(false);

  const modelOptions = useMemo(() => uniqueModels(lookupMatches), [lookupMatches]);
  const variantOptions = useMemo(
    () => (selectedModelId ? uniqueVariantsForModel(lookupMatches, selectedModelId) : []),
    [lookupMatches, selectedModelId]
  );
  const hasResolvedLookup = lookupMatches.length > 0;
  const selectedVariant = variantOptions.find((match) => variantName(match) === selectedVariantName);
  const yearOptions = selectedVariant
    ? yearsInRange(selectedVariant.fromYear, selectedVariant.toYear)
    : [];
  const canSave =
    Boolean(selectedModelId) && Boolean(selectedVariantName) && yearValue !== '';

  const applyVariantSelection = (match: VinLookupMatch) => {
    setSelectedVariantName(variantName(match));
    setMakeValue(match.make);
    setModelValue(match.model);
    setYearValue(match.estimatedManufacturingYear);
  };

  const applyModelSelection = (modelId: string, matches: VinLookupMatch[]) => {
    const modelMatch = uniqueModels(matches).find((match) => String(match.modelId) === modelId);
    if (!modelMatch) {
      return;
    }

    setSelectedModelId(modelId);
    setMakeValue(modelMatch.make);
    setModelValue(modelMatch.model);

    const nextVariants = uniqueVariantsForModel(matches, modelId);
    if (nextVariants.length === 1) {
      applyVariantSelection(nextVariants[0]);
      return;
    }

    setSelectedVariantName('');
    setYearValue('');
  };

  const resetResolvedFields = (nextMake = '', nextModel = '', nextYear: number | '' = '') => {
    setMakeValue(nextMake);
    setModelValue(nextModel);
    setYearValue(nextYear);
    setLookupMatches([]);
    setSelectedModelId('');
    setSelectedVariantName('');
  };

  useEffect(() => {
    if (open) {
      setVinValue(vin ?? '');
      resetResolvedFields(make ?? '', model ?? '', year ?? '');
      setError(null);
      setLookupLoading(false);
    }
  }, [open, vin, make, model, year]);

  useEffect(() => {
    if (!open || !imageId || !vehicleVin) {
      setImageUrl(undefined);
      return;
    }

    let cancelled = false;
    let objectUrl: string | undefined;

    const fetchImage = async () => {
      setImageLoading(true);
      setImageUrl(undefined);
      try {
        const blob = await FetchImageFromBackoffice<Blob>(
          `portal/files/${vehicleVin}/files/${imageId}`
        );
        objectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setImageUrl(objectUrl);
      } catch (fetchError) {
        console.warn('Inspection image fetch failed:', fetchError);
        if (!cancelled) {
          setImageUrl(undefined);
        }
      } finally {
        if (!cancelled) {
          setImageLoading(false);
        }
      }
    };

    fetchImage();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [open, imageId, vehicleVin]);

  const handleVinChange = (nextVin: string) => {
    setVinValue(nextVin);
    if (lookupMatches.length > 0) {
      resetResolvedFields(make ?? '', model ?? '', year ?? '');
    }
  };

  const handleLookup = async () => {
    const trimmedVin = vinValue.trim();
    setError(null);
    setLookupLoading(true);

    try {
      const data = await lookupVehicleByVin(trimmedVin);
      const matches = data?.matches ?? [];
      if (matches.length === 0) {
        resetResolvedFields(make ?? '', model ?? '', year ?? '');
        setError('The VIN could not be verified. Edit the VIN and try Look up VIN again.');
        return;
      }

      setLookupMatches(matches);

      const models = uniqueModels(matches);
      if (models.length === 1) {
        applyModelSelection(String(models[0].modelId), matches);
        return;
      }

      const uniqueMakeIds = new Set(models.map((match) => String(match.makeId)));
      setMakeValue(uniqueMakeIds.size === 1 ? models[0].make : '');
      setModelValue('');
      setSelectedModelId('');
      setSelectedVariantName('');
      setYearValue('');
    } catch (lookupError) {
      console.error(lookupError);
      resetResolvedFields(make ?? '', model ?? '', year ?? '');
      setError(describeVinLookupError(
        lookupError,
        'The VIN could not be verified. Edit the VIN and try Look up VIN again.'
      ));
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSave = () => {
    if (!selectedModelId || !selectedVariantName || yearValue === '') {
      return;
    }

    onSaved?.({
      vin: vinValue.trim(),
      make: makeValue,
      model: modelValue,
      year: yearValue,
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Verify VIN"
      size="large"
      footer={
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            onClick={onClose}
            disabled={lookupLoading}
            extend={{
              color: theme.colors.grey,
              fontSize: 16,
              padding: '8px',
            }}
          >
            CANCEL
          </Button>
          <Button
            onClick={handleSave}
            disabled={lookupLoading || !canSave}
            extend={{
              color: theme.colors.primary,
              fontSize: 16,
              padding: '8px',
            }}
          >
            SAVE
          </Button>
        </Box>
      }
    >
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : hasResolvedLookup ? (
        <Alert
          severity="info"
          sx={{
            mb: 2,
            bgcolor: theme.colors.primaryLight,
            color: theme.colors.text,
            '& .MuiAlert-icon': { color: theme.colors.primary },
          }}
        >
          Select the correct model, variant and year from the options returned by the VIN lookup to continue.
        </Alert>
      ) : null}
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, alignItems: 'flex-start' }}>
        {imageId && vehicleVin ? (
          <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {imageLoading ? (
              <CircularProgress size={40} />
            ) : imageUrl ? (
              <img
                src={imageUrl}
                alt=""
                style={{ display: 'block', maxWidth: '60vw', maxHeight: '80vh' }}
              />
            ) : null}
          </Box>
        ) : null}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 240 }}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <TextField
                label="VIN"
                value={vinValue}
                onChange={(event) => handleVinChange(event.target.value)}
                size="small"
                autoFocus
                disabled={lookupLoading}
                sx={{ flex: 1 }}
              />
              <MuiButton
                variant="outlined"
                onClick={handleLookup}
                size="small"
                disabled={lookupLoading || !vinValue.trim()}
                sx={{
                  height: 40,
                  minWidth: 132,
                  whiteSpace: 'nowrap',
                  color: theme.colors.primary,
                  borderColor: theme.colors.primary,
                  '&:hover': {
                    borderColor: theme.colors.primary,
                    backgroundColor: theme.colors.primaryLight,
                  },
                }}
              >
                {lookupLoading ? <CircularProgress size={18} color="inherit" /> : 'LOOK UP VIN'}
              </MuiButton>
            </Box>
          </Box>
          <SubTitle>
            Vehicle information
          </SubTitle>
          <TextField
            label="Make"
            value={makeValue}
            size="small"
            fullWidth
            slotProps={readOnlySlotProps}
          />
          {hasResolvedLookup ? (
            <FormControl size="small" fullWidth>
              <InputLabel shrink>Model</InputLabel>
              <Select
                label="Model"
                notched
                displayEmpty
                value={selectedModelId}
                renderValue={(value) => {
                  if (!value) {
                    return 'Select model';
                  }
                  return modelOptions.find((match) => String(match.modelId) === value)?.model ?? 'Select model';
                }}
                onChange={(event) => applyModelSelection(String(event.target.value), lookupMatches)}
              >
                {modelOptions.map((match) => (
                  <MenuItem key={String(match.modelId)} value={String(match.modelId)}>
                    {match.model}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              label="Model"
              value={modelValue}
              size="small"
              fullWidth
              slotProps={readOnlySlotProps}
            />
          )}
          {hasResolvedLookup ? (
            <FormControl size="small" fullWidth disabled={!selectedModelId}>
              <InputLabel shrink>Variant</InputLabel>
              <Select
                label="Variant"
                notched
                displayEmpty
                value={selectedVariantName}
                renderValue={(value) => (value ? String(value) : 'Select variant')}
                onChange={(event) => {
                  const match = variantOptions.find((item) => variantName(item) === event.target.value);
                  if (match) {
                    applyVariantSelection(match);
                  }
                }}
              >
                {variantOptions.map((match) => (
                  <MenuItem key={variantName(match)} value={variantName(match)}>
                    {match.variant}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          {hasResolvedLookup ? (
            <FormControl size="small" fullWidth disabled={!selectedVariantName}>
              <InputLabel shrink>Year</InputLabel>
              <Select
                label="Year"
                notched
                displayEmpty
                value={yearValue === '' ? '' : String(yearValue)}
                renderValue={(value) => (value ? String(value) : 'Select year')}
                onChange={(event) => setYearValue(Number(event.target.value))}
              >
                {yearOptions.map((option) => (
                  <MenuItem key={option} value={String(option)}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              label="Year"
              value={String(yearValue)}
              size="small"
              fullWidth
              slotProps={readOnlySlotProps}
            />
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default VinVerificationModal;
